// src/utils/sla.js
import { db } from "../firebase/config";
import { doc, getDoc, updateDoc } from "firebase/firestore";

/* ---------- helpers ---------- */

const clampInt = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.floor(n);
};
function deriveSnapshot(priority = "low", policy) {
  const key = String(priority || "low").toLowerCase();
  const t = policy?.targets?.[key] ||
    policy?.targets?.low || { firstResponseMins: 60, resolutionMins: 240 };
  return {
    priorityKey: key,
    firstResponseMins: clampInt(t.firstResponseMins),
    resolutionMins: clampInt(t.resolutionMins),
    pauseWhenWaitingOnCustomer: !!policy?.pauseWhenWaitingOnCustomer,
    pauseStatus: policy?.pauseStatus || "Waiting on Customer",
    warnMinutesBeforeBreach: clampInt(policy?.warnMinutesBeforeBreach ?? 30),
    policyAppliedAt: new Date(),
  };
}

const toMs = (t) => {
  if (!t) return null;
  if (t?.toDate) return t.toDate().getTime(); // Firestore Timestamp
  if (t instanceof Date) return t.getTime();
  const d = new Date(t);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
};
const nowMs = () => Date.now();

/* ---------- policy ---------- */
export async function getSlaPolicy(companyId) {
  const ref = doc(db, `companies/${companyId}/settings/slaPolicy`);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return snap.data();
}

/* ---------- core calculation ---------- */
//function getTargets(priority = "low", policy) {
//  const key = (priority || "low").toLowerCase();
//  return (
//  policy?.targets?.[key] ||
//  policy?.targets?.low || { firstResponseMins: 60, resolutionMins: 240 }
// );
//}

function currentPausedMs(ticket, policy) {
  const pauseAllowed =
    ticket?.sla?.policyTargets?.pauseWhenWaitingOnCustomer ??
    policy?.pauseWhenWaitingOnCustomer;
  if (!pauseAllowed) return 0;
  const total = ticket?.sla?.totalPausedMs || 0;
  const running =
    ticket?.sla?.paused && ticket?.sla?.pausedAt
      ? nowMs() - toMs(ticket.sla.pausedAt)
      : 0;
  return total + running;
}

// start + (mins) - paused
function dueFrom(startMs, mins, pausedMs) {
  const m = (mins || 0) * 60 * 1000;
  return new Date(Math.max(0, startMs + m - (pausedMs || 0)));
}

/* ---------- recompute and persist due dates ---------- */
export async function ensureTicketSla(companyId, ticketId, ticket, policy) {
  if (!ticket?.createdAt) return;

  const start = toMs(ticket.createdAt);
  // Prefer per-ticket snapshot; if missing and policy supplied, create it.
  let snap = ticket?.sla?.policyTargets || null;
  if (!snap && policy) {
    snap = deriveSnapshot(ticket.priority, policy);
  }
  if (!snap) return; // Nothing to do if no snapshot and no policy

  const paused = currentPausedMs(ticket, {
    pauseWhenWaitingOnCustomer: snap.pauseWhenWaitingOnCustomer,
  });

  const firstResponseDueAt = dueFrom(start, snap.firstResponseMins, paused);
  const resolutionDueAt = dueFrom(start, snap.resolutionMins, paused);

  const ref = doc(db, `companies/${companyId}/tickets`, ticketId);
  const payload = {
    "sla.firstResponseDueAt": firstResponseDueAt,
    "sla.resolutionDueAt": resolutionDueAt,
    "sla.policyPriority": (ticket.priority || "low").toLowerCase(),
  };
  // Only write the snapshot once
  if (!ticket?.sla?.policyTargets && snap) {
    payload["sla.policyTargets"] = snap;
  }
  await updateDoc(ref, payload);
}

/* ---------- first response ---------- */
export async function markFirstResponseIfNeeded(
  companyId,
  ticketId,
  ticket,
  policy
) {
  if (ticket?.sla?.firstResponseAt) return; // already set
  const start = toMs(ticket.createdAt);
  const snap =
    ticket?.sla?.policyTargets ||
    (policy && deriveSnapshot(ticket.priority, policy));
  if (!snap) return;
  const paused = currentPausedMs(ticket, {
    pauseWhenWaitingOnCustomer: snap.pauseWhenWaitingOnCustomer,
  });
  const frDue = dueFrom(start, snap.firstResponseMins, paused);
  const firstResponseAt = new Date();

  const ref = doc(db, `companies/${companyId}/tickets`, ticketId);
  await updateDoc(ref, {
    "sla.firstResponseAt": firstResponseAt,
    "sla.breachedFirstResponse": firstResponseAt.getTime() > frDue.getTime(),
  });

  // refresh due dates (in case pause was running)
  await ensureTicketSla(
    companyId,
    ticketId,
    { ...ticket, sla: { ...(ticket.sla || {}), firstResponseAt } },
    policy
  );
}

/* ---------- pause / unpause ---------- */
export async function enterPause(companyId, ticketId) {
  const ref = doc(db, `companies/${companyId}/tickets`, ticketId);
  await updateDoc(ref, {
    "sla.paused": true,
    "sla.pausedAt": new Date(),
  });
}

export async function exitPause(companyId, ticketId, ticket, policy) {
  const prev = ticket?.sla || {};
  const started = toMs(prev.pausedAt);
  const add = started ? Math.max(0, nowMs() - started) : 0;
  const newTotal = (prev.totalPausedMs || 0) + add;

  const ref = doc(db, `companies/${companyId}/tickets`, ticketId);
  await updateDoc(ref, {
    "sla.paused": false,
    "sla.pausedAt": null,
    "sla.totalPausedMs": newTotal,
  });

  // recompute due dates after pause ends
  await ensureTicketSla(
    companyId,
    ticketId,
    {
      ...ticket,
      sla: { ...prev, paused: false, pausedAt: null, totalPausedMs: newTotal },
    },
    policy
  );
}

/* ---------- resolve ---------- */
export async function markResolved(companyId, ticketId, ticket, policy) {
  const start = toMs(ticket.createdAt);
  const snap =
    ticket?.sla?.policyTargets ||
    (policy && deriveSnapshot(ticket.priority, policy));
  if (!snap) return;
  const paused = currentPausedMs(ticket, {
    pauseWhenWaitingOnCustomer: snap.pauseWhenWaitingOnCustomer,
  });
  const resDue = dueFrom(start, snap.resolutionMins, paused);
  const resolvedAt = ticket.resolvedAt
    ? new Date(ticket.resolvedAt)
    : new Date();

  const ref = doc(db, `companies/${companyId}/tickets`, ticketId);
  await updateDoc(ref, {
    "sla.breachedResolution": resolvedAt.getTime() > resDue.getTime(),
  });

  // also refresh due-dates snapshot for completeness
  await ensureTicketSla(companyId, ticketId, ticket, policy);
}

/* ---------- convenience for UI ---------- */
export function secondsUntil(date) {
  const ms = date instanceof Date ? date.getTime() : toMs(date);
  return Math.floor((ms - nowMs()) / 1000);
}
